/**
 * api.ts · acceso a datos de campañas sociales.
 *
 * 🔴 Toda llamada a `core.*` va con `.schema('core')` implícito: el cliente de
 * `@/lib/supabase` ya está creado con `db.schema = 'core'`.
 */
import { supabase } from '@/lib/supabase';
import type {
  CampaignSocialMessagesUpdate,
  CampaignsRow,
  SocialCampaignOutcome,
  SocialChannelRulesUpdate,
  TouchChannel,
} from '@/types/database.types';

import type {
  ChannelRule,
  DestinatarioPendiente,
  DueTouch,
  GeneratedTouch,
  SocialCommitResult,
  SocialPreviewResult,
  SocialReplySuggestion,
  SocialTouch,
} from './types';

// ── Lectura ─────────────────────────────────────────────────────────────────

/** Los toques de una campaña, en orden. */
export async function listTouches(campaignId: string): Promise<SocialTouch[]> {
  const { data, error } = await supabase
    .from('campaign_social_messages')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('step_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SocialTouch[];
}

/** Los toques de VARIAS campañas de una vez, para el listado agrupado. */
export async function listTouchesForCampaigns(
  campaignIds: string[],
): Promise<SocialTouch[]> {
  if (campaignIds.length === 0) return [];
  const { data, error } = await supabase
    .from('campaign_social_messages')
    .select('*')
    .in('campaign_id', campaignIds)
    .order('step_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as SocialTouch[];
}

/**
 * Los toques vencidos del usuario.
 *
 * 🔴 Sale de `core.v_social_touches_due` y de ningún otro lado. La vista filtra
 * por estado de campaña Y por resultado; replicar ese predicado del lado del
 * cliente es cómo el badge de la navegación y la bandeja terminan discrepando.
 */
export async function listDueTouches(): Promise<DueTouch[]> {
  const { data, error } = await supabase
    .from('v_social_touches_due')
    .select('*')
    .order('scheduled_for', { ascending: true });
  if (error) throw error;
  return (data ?? []) as DueTouch[];
}

/**
 * Actualiza una regla de canal.
 *
 * 🔴 Des-verificar al cambiar el límite sin fuente nueva NO se hace acá: lo hace
 * un trigger en la base. Si dependiera de esta función, cualquier otro camino de
 * escritura —la RPC de mañana, un script, el MCP— lo saltearía.
 */
export async function updateChannelRule(
  id: string,
  patch: SocialChannelRulesUpdate,
): Promise<ChannelRule> {
  const { data, error } = await supabase
    .from('social_channel_rules')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ChannelRule;
}

export async function listChannelRules(): Promise<ChannelRule[]> {
  const { data, error } = await supabase
    .from('social_channel_rules')
    .select('*')
    .order('channel', { ascending: true })
    .order('message_kind', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChannelRule[];
}

// ── Marcas del vendedor ─────────────────────────────────────────────────────

export async function markTouchSent(
  touchId: string,
  userId: string,
): Promise<SocialTouch> {
  return patchTouch(touchId, {
    status: 'sent',
    sent_at: new Date().toISOString(),
    marked_by: userId,
  });
}

export async function markTouchSkipped(
  touchId: string,
  userId: string,
): Promise<SocialTouch> {
  return patchTouch(touchId, {
    status: 'skipped',
    skipped_at: new Date().toISOString(),
    marked_by: userId,
  });
}

/** Guarda el texto editado conservando el original de la IA. */
export async function editTouchBody(
  touchId: string,
  body: string,
): Promise<SocialTouch> {
  return patchTouch(touchId, { body, was_edited: true });
}

export async function rescheduleTouch(
  touchId: string,
  scheduledFor: string,
): Promise<SocialTouch> {
  return patchTouch(touchId, { scheduled_for: scheduledFor });
}

/** Reprograma varios toques de una (la cascada del diálogo de fecha). */
export async function rescheduleMany(
  cambios: Array<{ id: string; scheduled_for: string }>,
): Promise<void> {
  for (const c of cambios) {
    await patchTouch(c.id, { scheduled_for: c.scheduled_for });
  }
}

async function patchTouch(
  id: string,
  patch: CampaignSocialMessagesUpdate,
): Promise<SocialTouch> {
  const { data, error } = await supabase
    .from('campaign_social_messages')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as SocialTouch;
}

// ── Resultado de la campaña ─────────────────────────────────────────────────

/** «Aceptó la solicitud»: resultado intermedio, la secuencia sigue viva. */
export async function markConnected(campaignId: string): Promise<CampaignsRow> {
  const { data, error } = await supabase
    .from('campaigns')
    .update({ social_outcome: 'connected', connected_at: new Date().toISOString() })
    .eq('id', campaignId)
    .select('*')
    .single();
  if (error) throw error;
  return data as CampaignsRow;
}

/**
 * Cierra la campaña: fija el resultado Y la archiva en la misma escritura.
 *
 * Va por RPC y no por dos updates desde acá porque si el segundo fallara, la
 * campaña quedaría `discarded` + `active` reclamando toques para siempre.
 */
export async function closeCampaign(
  campaignId: string,
  outcome: Extract<SocialCampaignOutcome, 'no_reply' | 'discarded'>,
): Promise<void> {
  const { error } = await supabase.rpc('close_social_campaign', {
    p_campaign_id: campaignId,
    p_outcome: outcome,
  });
  if (error) throw error;
}

/**
 * «Respondió»: crea el lead de esa red y fija el resultado, en una escritura.
 *
 * Idempotente del lado de la base: si la campaña ya tiene lead, devuelve ese.
 * Por eso no hace falta chequear antes desde acá — el chequeo del diálogo es
 * para no ofrecer la acción, no para evitar el duplicado.
 */
export async function markReplied(
  campaignId: string,
  firstMessage: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('mark_social_campaign_replied', {
    p_campaign_id: campaignId,
    p_first_message: firstMessage,
  });
  if (error) throw error;
  return data as string;
}

/** La conversación que originó esta campaña, si ya existe. */
export async function findCampaignLead(campaignId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .select('id')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data?.id as string | undefined) ?? null;
}

// ── Edges ───────────────────────────────────────────────────────────────────

/**
 * El edge no pudo saber a quién se le escribe y necesita que lo diga un humano.
 *
 * Es un error de flujo, no una falla: se lanza como excepción propia para que
 * el wizard la distinga de un problema del proveedor y abra la pregunta en vez
 * de mostrar un cartel rojo.
 */
export class DestinatarioDesconocido extends Error {
  // Campo explícito y no propiedad de parámetro: `erasableSyntaxOnly` prohíbe
  // el azúcar `constructor(readonly x)`.
  readonly detalle: DestinatarioPendiente;

  constructor(detalle: DestinatarioPendiente) {
    super(detalle.message ?? '¿A nombre de quién escribimos?');
    this.name = 'DestinatarioDesconocido';
    this.detalle = detalle;
  }
}

async function invokeEdge<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    // El cuerpo del error trae el mensaje útil del edge; `error.message` solo
    // dice «non-2xx status code».
    const cuerpo = await leerCuerpo(error);
    if (cuerpo?.error === 'recipient_unknown') {
      throw new DestinatarioDesconocido(cuerpo as unknown as DestinatarioPendiente);
    }
    throw new Error(cuerpo?.message ?? cuerpo?.error ?? error.message);
  }
  return data as T;
}

interface CuerpoDeError {
  error?: string;
  message?: string;
}

async function leerCuerpo(error: unknown): Promise<CuerpoDeError | null> {
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return null;
  try {
    return (await ctx.json()) as CuerpoDeError;
  } catch {
    return null;
  }
}

export function previewSocialSequence(input: {
  prospect_id: string;
  channel: TouchChannel;
  /** 🔴 Obligatorio: define a QUIÉN se le escribe, no solo dónde se guarda. */
  social_contact_id: string;
  ai_brief?: string | null;
  /** Respuesta del humano cuando el handle no dejaba ver de quién es. */
  recipient_name?: string | null;
  /** El humano confirmó que no se sabe: se escribe sin nombre propio. */
  recipient_unknown_ack?: boolean;
}): Promise<SocialPreviewResult> {
  return invokeEdge<SocialPreviewResult>('core-generate-social-sequence', {
    ...input,
    action: 'preview',
  });
}

export function commitSocialSequence(input: {
  prospect_id: string;
  channel: TouchChannel;
  social_contact_id: string;
  ai_brief?: string | null;
  recipient_name?: string | null;
  recipient_unknown_ack?: boolean;
  campaign_name?: string | null;
  campaign_id?: string | null;
  activate?: boolean;
  touches?: GeneratedTouch[];
}): Promise<SocialCommitResult> {
  return invokeEdge<SocialCommitResult>('core-generate-social-sequence', {
    ...input,
    action: 'commit',
  });
}

export function generateSocialReplies(
  conversationId: string,
): Promise<{ suggestions: SocialReplySuggestion[]; char_limit: number }> {
  return invokeEdge('core-generate-social-reply', {
    conversation_id: conversationId,
  });
}
